import _ from '../../utils/underscore';// eslint-disable-line id-length
import DOM, {DomEventSubscription} from '../../utils/dom';
import Event from '../../utils/event';
import Device from '../../utils/device/device';
import BaseService from '../../base/base_service';
import ToasterStorage from './toaster_storage/toaster_storage';
import ToasterFactory from './toaster_factory/toaster_factory';
import {LIB_CSS_IDS, POSITION, TOAST_TYPES} from './toaster_service_consts';
import { SYSTEM_EVENTS, USER_EVENTS } from '../../events/events';
import IToasterOptions from './toaster_options_interface';
import IToasterService, {IShowToasterNotificationParams} from './toaster_service_interface';
import { IziToastSettings, IziToastPosition } from 'izitoast';

export interface IUCCToasterParams {
	Type?: TOAST_TYPES;
	MessageID: string | number;
	GameTypeID?: string | number;
	Title: string;
	SubTitle: string;
	DurationInSec: number;
	MobileImage: string;
	PCImage: string;
	IconImagePath: string;
	customUIConfig: string;
	ResponseTopicPath: string;
	MetaDataParams: string | null;
	analyticsData: {
		category: string;
		action: string;
		label?: string;
	};
	LangIso3: string;
	close: boolean;
	buttonText: string;
	callback: () => void;
	layout: number;
}

export interface IToasterBuilderOptions {
	id: string | number;
	cgt?: string | number;
	title: string;
	message: string;
	timeout: number;
	image?: string;
	showImageByDefault: boolean;
	customUIConfig?: string;
	responseTopicPath?: string;
	analyticsData?: {
		category: string;
		action: string;
		label?: string;
	};
	lang?: string;
	domListeners?: DomEventSubscription[];
	close?: boolean;
	buttonText?: string;
	callback?: () => void;
	layout?: number;
	type?: TOAST_TYPES;
}


export default class ToasterService extends BaseService implements IToasterService {

	/**
	 * @static
	 * @type {string}
	 */
	static CLASS = 'ToasterService';


	/**
	 * ToasterStorage instance to be used
	 * @type {ToasterStorage}
	 * @private
	 */
	_toasterStorage = null;

	_receivedToasterEvent = null;
	_appResumeEvent = null;

	/**
	 * iziToast toaster HTML container -> only used for hybrid
	 * @type (HTMLElement)
	 * @private
	 */
	_toasterContainer = null;


	/**
	 * ToasterFactory instance which will be used to get toaster configurations and keep track of toasters
	 *
	 * @type {ToasterFactory}
	 * @private
	 */
	_toasterFactory: ToasterFactory = null;


	/**
	 *
	 * @param params
	 */
	init(params) {
		super.init(params);
		this._toasterStorage = ToasterStorage.create();
		this._toasterFactory = ToasterFactory.create();
		this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._authStateChange);
		this.onSystemEvent(SYSTEM_EVENTS.ON_INIT_COMPLETE, () => {
			if (this._isAuthorized()) {
				this._login(false);
				this._showSavedToasts();
			}
		});
	}

	private loadIziToastLib() {
		return import("izitoast").then(m => m.default);
	}

	/**
	 * Shows a toaster game notification
	 * @param config
	 * @returns {ToasterService}
	 */
	showToaster(config: IShowToasterNotificationParams): this {
		const libParams = this._createBuilderOptions(config);

		if(this._isGameToaster(libParams)) {
			this._handleGameToasters([libParams]);
		} else {
			const notificationCfg = this._prepareToastParams(libParams);
			this._showToast(notificationCfg);
		}

		return this;
	}

	showActionableToaster(config: IShowToasterNotificationParams): this {
		config.Type = TOAST_TYPES.ACTION;
		config.ShowDefaultImage = false;
		return this.showToaster(config);
	}

	showAckToaster(config: IShowToasterNotificationParams): this {
		config.Type = TOAST_TYPES.ACKNOWLEDGE;
		config.ShowDefaultImage = false;
		return this.showToaster(config);
	}

	showToasterWithInjectedContent(contentInjector: (toasterBody: HTMLElement, removeTitle: () => void) => void, options: IToasterOptions) {
        let toasterRootElement: HTMLElement = null;

		const onOpening = (iziToastSettings: IziToastSettings, toasterHTML: HTMLElement) => {

			toasterRootElement = toasterHTML;
			let toasterBody = toasterHTML.querySelector(LIB_CSS_IDS.toastBody) as HTMLElement;
			if (!toasterBody) {
				this.logger.error(`Can't find toaster body selector ${LIB_CSS_IDS.toastBody}!`);
				return;
			}

			// we let the consumer of `showToasterWithInjectedContent` to define the logic of how the content is injected.
			// because sdk components are injected in a way but orbit components have different logic
			// so in contentInjector the consumer can do for example: `toasterBody.appendChild(whatever element he wants);` and/or `toasterBody.addEventListener("click", () => {})`
			// calling `removeTitle()` will remove the toaster's title
			contentInjector(toasterBody, () => {
				// remove title to avoid tricks on component CSS
				const toasterTitle = toasterHTML.querySelector(LIB_CSS_IDS.toastTexts) as HTMLElement;
				toasterTitle.parentElement.removeChild(toasterTitle);
			});

		};

		const toasterConfig: IziToastSettings = {
			class: 'CGPToasterLayout cgp-toaster-component',
			position: this.serviceFactory.languageService().getIsRTL() ? 'bottomLeft' : 'bottomRight',
			progressBar: false,
			timeout: 0,
			drag: false,
			close: _.isNullOrUndefined(options.close) ? true : _.toBoolean(options.close),
			theme: 'dark',
			onOpening: onOpening,
			onOpened: options.onOpened,
			onClosed: options.onClosed
		};

		if (options && options.additionalClass) {
			toasterConfig['class'] += ' ' + options.additionalClass;
		}

		if (options && options.position) {
			toasterConfig.position = POSITION[options.position] as IziToastPosition;
		}

		if (options && options.timeoutInSecs) {
			toasterConfig.timeout = options.timeoutInSecs * 1000;
		}

		if(options && options.zindex) {
			toasterConfig.zindex = options.zindex;
		}

		this._showToast(toasterConfig);

		return (closedBy: string = 'toasterDisposer') => {
			//the parameters of hide() method are reversed in the TypeScript definition of iziToast
			this.loadIziToastLib().then((iziToast) => {
				// first check if toaster was closed. if the toasterRootElement was detached from DOM, that means it was closed
				// check detached state by calling `toasterRootElement.closest("body")`. It will return null for detached state or the body element for mounted.
				// if we don't do this check, and call the disposer twice, the second time iziToast will throw this error: Uncaught TypeError: Cannot convert undefined or null to object
				if (toasterRootElement.closest("body") !== null) {
					iziToast.hide(toasterConfig, toasterRootElement as HTMLDivElement, closedBy);
				}
			});
		}
	}

	/**
	 * Hides a specific toast
	 *
	 * @param toast iziToastObject or toaster HTML element
	 * @returns {ToasterService}
	 */
	hideToast(toast: HTMLDivElement | string, options: IziToastSettings): this {
		options = options || { onClosed: this._onToastHide.bind(this) };
		this.loadIziToastLib().then(iziToast => {
			iziToast.hide(options, toast, 'hideToast');
		});
		return this;
	}

	/**
	 * Removes (destroys) all toasts
	 *
	 * @returns {ToasterService}
	 */
	removeAllToasts() {
		this.loadIziToastLib().then(iziToast => {
			iziToast.destroy();
		});
		return this;
	}

	/**
	 * Translates the parameters received through firebase to what the wrapper uses ( shorter property names mostly )
	 * @param params (Array) -> Message parameters received from firebase
	 */
	onToastMessageReceived(params) {
		// this was added for qa as an alternative to firebase ( in case of firebase received messages, this condition is redundant )
		if (!this._isAuthorized()) {
			this.logger.debug('TOASTER [onToastMessageReceived] :: NOT LOGGED IN, QUITTING');
			return;
		}

		// parse toaster array and show all the toasters
		const gameToasters: IToasterBuilderOptions[] = [];
		for (let i = 0; i < params.length; i++) {
			// translate firebase message params to what the wrapper uses
			const libParams = this._createBuilderOptions(params[i]);

			// override timeout if the toaster will open a game and add toaster to the game toaster queue
			if (!_.isUndefined(libParams.cgt)) {
				libParams.cgt = parseInt(libParams.cgt.toString());
				gameToasters.push(libParams);
				continue;
			}
			this._handleToaster(libParams);
		}
		if (gameToasters.length > 0) {
			this._handleGameToasters(gameToasters);
		}
	}

	/**
	 *  Shows a toast
	 *
	 * @param toastConfig
	 * @returns {ToasterService}
	 */
	_showToast(toastConfig: IziToastSettings) {
		this.loadIziToastLib().then(iziToast => {
			iziToast.show(toastConfig);
		});

		return this;
	}

	/**
	 * Decides if the toaster will be shown or saved to the pending toaster queue -> this applies only to temporary and acknowledge toasters
	 * Displays a toaster using toasterParams
	 * @param toasterParams toaster config
	 * @private
	 */
	_handleToaster(toasterParams) {
		const isTimeToast = !_.isUndefined(toasterParams.timeout) && toasterParams.timeout > 0;
		if (!isTimeToast) {
			if (!this.serviceFactory.visibilityDetectorService().isActive()) {
				this._toasterStorage.saveToPending(toasterParams);
				this.logger.debug('TOASTER :: SAVED TO LOCAL STORAGE ', toasterParams);
				return;
			}
			this._toasterStorage.saveToDisplayed(toasterParams.id);
		}

		this._showToast(this._prepareToastParams(toasterParams));
	}


	/**
	 * Gets the game list from IntegrationService and shows the game toasters if the game's cgt is not restricted
	 * @param toasterList the list of game toasters config objects to be handled
	 */
	_handleGameToasters(toasterList: IToasterBuilderOptions[]) {
		return this.serviceFactory.gamesInformationService().getAllGamesInfo().then(gamesInfo => {
			const shownGameToasters = [];
			for (let i = 0; i < toasterList.length; i++) {
				const toaster = toasterList[i];
				if (gamesInfo[toaster.cgt]) {
					// Add button for game toaster
					toaster.type=TOAST_TYPES.ACTION;
					toaster.buttonText = toaster.buttonText || this.serviceFactory.languageService().translation('game_item_play_btn');
					toaster.callback = () => {
						this.serviceFactory.gameService().openRealGame(gamesInfo[toaster.cgt]);
					};

					shownGameToasters.push(toaster);
					this._handleToaster(toaster);
				}
			}

			return shownGameToasters;
		});
	}

	_isGameToaster(toasterConfig: {cgt?: number | string | null | undefined}): boolean {
		return !_.isNullOrUndefined(toasterConfig.cgt);
	}

	/**
	 * Adjusts the parameters received through firebase to iziToast & our BRD requirements for any kind of toaster
	 */
	_prepareToastParams(params: IToasterBuilderOptions): IziToastSettings {
		return Object.assign(
			{
				onOpening: this._onBeforeOpen.bind(this),
				onOpened: this._onToastShow.bind(this),
				onClosing: this._handleToasterClose.bind(this),
				onClosed: this._onToastHide.bind(this)
			},
			this._toasterFactory.buildToasterConfiguration(params)
		);
	}


	/**
	 * Shows the toaster queue for the current user and deletes the entry ( for the current user ).
	 * @private
	 */
	_showSavedToasts() {
		const pendingToasters = this._toasterStorage.getPendingToasters();
		while (pendingToasters.length > 0) {
			this._showToast(this._prepareToastParams(pendingToasters.shift()));
		}
	}


	/**
	 * Detects the users' login / logout state
	 * @private
	 */
	_authStateChange() {
		this.logger.debug('TOASTER :: AUTH STATE CHANGE TO -> ', this._isAuthorized());
		this._isAuthorized() ? this._login() : this._logout();
	}


	/**
	 * Login was successful so toasters can be enabled if tab is in focus else will wait for focus gain and then enable toasters
	 * @private
	 */
	_login(clearStorage = true) {
		this.logger.debug('TOASTER :: LOGIN HANDLER');
		if (clearStorage) {
			this._toasterStorage.clear();
		}

		this._receivedToasterEvent = this.onSystemEvent(SYSTEM_EVENTS.SESSION_EVENT_RECEIVED_TOASTER, this.onToastMessageReceived);
		this._appResumeEvent = this.onSystemEvent(SYSTEM_EVENTS.APP_RESUMED, this._showSavedToasts);
	}


	/**
	 * On logout, all toasts will be closed, regardless of type
	 *
	 * @private
	 */
	_logout() {
		this.logger.debug('TOASTER :: LOGOUT HANDLER');
		this._toasterStorage.clear();
		this.removeAllToasts();

		this._receivedToasterEvent && this._receivedToasterEvent.off();
		this._appResumeEvent && this._appResumeEvent.off();
	}


	/**
	 * Applies style / css rules according to the displayed toaster
	 * @param iziToastObj iziToast object
	 * @param toasterHTML HTML element
	 * @private
	 */
	_applyCustomDomConfig(iziToastObj, toasterHTML) {
		iziToastObj.applyDomConfig.call(this._toasterFactory, toasterHTML, this.serviceFactory.themesService());
		if (Device.hybrid.inCasino && !this._toasterContainer) {
			this._toasterContainer = document.getElementsByClassName(LIB_CSS_IDS.container)[0];
			this._toasterContainer.classList.add('cgp-hybrid');
		}
	}


	/**
	 * Callback for when a toast completed its entrance animation
	 *
	 * @param iziToastObject - iziToast instance
	 * @param toasterHTML - the toast
	 * @private
	 */
	_onToastShow(iziToastObject, toasterHTML) {
		if(iziToastObject && iziToastObject.analyticsData) {
			this._sendAnalytics(iziToastObject.analyticsData.category, iziToastObject.analyticsData.action, iziToastObject.analyticsData.label)
		}

		// General event when toaster shown
		this._sendToasterOpenedAnalytics(iziToastObject);

		Event.fire(SYSTEM_EVENTS.TOAST_SHOW, { toast: toasterHTML, config: iziToastObject });
	}

	_sendToasterOpenedAnalytics(iziToastObject) {
		const cid = this.serviceFactory.userService().getCID() || 'annonymous';

		const analyticsData = {
			category: 'Communications',
			action:  iziToastObject.id + '_Show',
			label : cid + '_' + Date.now().toString()
		}

		this._sendAnalytics(analyticsData.category, analyticsData.action, analyticsData.label)
	}

	_sendAnalytics(category, action, label="toaster opened") {
		let analyticsObj = {
			category: category,
			action: action,
			label: label,
		};

		Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, analyticsObj);
	}

	/**
	 * Adds custom fonts and listeners for newly created toasters
	 * Prevents default for touchstart(mousedown) / touchend(mouseup) events if the touch point is not over the close button
	 * and stops / resumes the timeout progress ( where applicable ).
	 * @param iziToastObject iziToast object
	 * @param toasterHTML toaster HTML element
	 * @private
	 */
	_onBeforeOpen(iziToastObject, toasterHTML) {
		this._applyCustomDomConfig(iziToastObject, toasterHTML);
		Event.fire(SYSTEM_EVENTS.TOAST_CREATED, { toast: toasterHTML, config: iziToastObject });

		if (iziToastObject.close) {
			const closeBtn = this._getToasterElement(toasterHTML);

			if (Device.isMobile() && iziToastObject.progressBar) {
				/**
				 * Used by temporary toasters on mobile.
				 * Prevents default for touchstart(mousedown) / touchend(mouseup) events if the touch point is not over the close button -> stops / resumes the timeout progress.
				 * @param event touch event
				 */
				const toasterTouchHandler = event => {
					this.loadIziToastLib().then(iziToast => {
						switch (event.type) {
							case USER_EVENTS.ON_TOUCH_START:
								iziToast.progress(iziToastObject, toasterHTML).pause();
								break;
							case USER_EVENTS.ON_TOUCH_END:
								iziToast.progress(iziToastObject, toasterHTML).resume();
								break;
						}
					});
				};

				iziToastObject.domListeners.push(DOM.addEventListener(toasterHTML, USER_EVENTS.ON_TOUCH_START, toasterTouchHandler));
				iziToastObject.domListeners.push(DOM.addEventListener(toasterHTML, USER_EVENTS.ON_TOUCH_END, toasterTouchHandler));
			}

			closeBtn.onclick = () => {
				Event.fire(SYSTEM_EVENTS.TOAST_CLOSE_BUTTON, {
					toastObj: iziToastObject,
					html: toasterHTML
				});
			}
		}

		this._addToasterCSSImage(iziToastObject, toasterHTML);
	}


	/**
	 * Attaches the toaster css image class to the cover element
	 * @param toaster
	 * @param toasterHTML
	 * @private
	 */
	_addToasterCSSImage(toaster, toasterHTML) {
		if (!_.isNullOrUndefined(toaster.cssImage)) {
			const toasterImgElement = toasterHTML.querySelector(LIB_CSS_IDS.cover);
			DOM.removeStyle(toasterImgElement, 'background-image')
				.addClass(toasterImgElement, toaster.cssImage);
		}
	}


	/**
	 * Makes temporary toasters ignore touch events on mobile ( pause on hold feature )
	 * @param iziToastObject iziToast object
	 * @param toasterHTML toaster HTML element
	 * @private
	 */
	_handleToasterClose(iziToastObject, toasterHTML) {
		while (iziToastObject.domListeners.length > 0) {
			iziToastObject.domListeners[0].remove();
			iziToastObject.domListeners.shift();
		}

		if (Device.isMobile() && iziToastObject.progressBar) {
			DOM.addStyle(toasterHTML, 'pointer-events', 'none');
		}
	}


	/**
	 * Callback for when a toast completed its exit animation and will be removed
	 *
	 * @param iziToastObject - iziToast instance
	 * @param toasterHTML - the toast
	 * @private
	 */
	_onToastHide(iziToastObject, toasterHTML) {
		Event.fire(SYSTEM_EVENTS.TOAST_HIDE, { toast: toasterHTML, config: iziToastObject });
		this._toasterFactory.removeToasterConfiguration(iziToastObject.id || toasterHTML.toasterID);
	}



	/**
	 * Attempts to find a toaster HTML child according to the provided css class
	 * @param toasterHTML HTML element
	 * @param libIdent css class
	 * @returns {*} an HTML element or null
	 * @private
	 */
	_getToasterElement(toasterHTML, libIdent = LIB_CSS_IDS.closeBtn) {
		const childrenNo = toasterHTML.childNodes.length;
		for (let index = 0; index < childrenNo; index++) {
			if (toasterHTML.childNodes[index].className === libIdent) {
				return toasterHTML.childNodes[index];
			}
		}
		return null;
	}

	/**
	 * @returns {*|boolean}
	 * @private
	 */
	_isAuthorized() {
		return this.serviceFactory.oAuth2Service().isAuthorized();
	}

	/**
	 * maps the format received by UCC to what iziToast uses
	 */
	_createBuilderOptions(config: Partial<IUCCToasterParams & IShowToasterNotificationParams>): IToasterBuilderOptions {
		const metaData = _.isNull(config.MetaDataParams) ? {} : _.safeJSON(config.MetaDataParams, {});

		const result: IToasterBuilderOptions = {
			id: config.MessageID,
			cgt: metaData.GameTypeID || config.GameTypeID,
			title: config.Title,
			message: config.SubTitle,
			timeout: config.DurationInSec * 1000 || 0,
			image: (Device.isMobile() ? config.MobileImage : config.PCImage) || config.IconImagePath,
			customUIConfig: config.customUIConfig,
			responseTopicPath: config.ResponseTopicPath,
			analyticsData: config.analyticsData,
			lang: config.LangIso3, // not used
			domListeners: [],
			close: config.close,
			buttonText: config.buttonText,
			callback: config.callback,
			layout: config.layout,
			showImageByDefault: config.ShowDefaultImage
		};

		if (config.Type) {
			result.type = config.Type;
		}

		return _.removeEmptyPropertiesfromObject(result) as IToasterBuilderOptions;
	}
}
